import React, { FC, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Box,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { useFinancialReports } from '../financialReports.context';
import {
  ExpenseCategory,
  ScholarshipStatus,
  BudgetStatus,
} from '../financialReports.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import PaginatedGrid from 'src/views/components/paginatedGrid/PaginatedGrid';

// Pending Fees Grid Component
export const PendingFeesGrid: FC = () => {
  const { translateText } = useTranslation();
  const { state, formatCurrency } = useFinancialReports();
  const { pendingFees } = state;

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'studentName',
      headerName: translateText(
        'finance.reports.feeCollection.columns.studentName'
      ),
      width: 200,
    },
    {
      field: 'class',
      headerName: translateText(
        'finance.reports.feeCollection.columns.class'
      ),
      width: 120,
    },
    {
      field: 'amountDue',
      headerName: translateText(
        'finance.reports.feeCollection.columns.amountDue'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatCurrency(params.row.amountDue)}
        </Typography>
      ),
    },
    {
      field: 'dueDate',
      headerName: translateText(
        'finance.reports.feeCollection.columns.dueDate'
      ),
      width: 130,
    },
    {
      field: 'daysOverdue',
      headerName: translateText(
        'finance.reports.feeCollection.columns.daysOverdue'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={`${params.row.daysOverdue} ${translateText('finance.reports.feeCollection.days')}`}
          color={params.row.daysOverdue > 7 ? 'error' : 'warning'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: translateText(
        'finance.reports.feeCollection.columns.actions'
      ),
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip
          title={translateText(
            'finance.reports.feeCollection.sendReminder'
          )}
        >
          <IconButton size="small">
            <Email />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [translateText, formatCurrency]);

  return (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translateText('finance.reports.feeCollection.pendingFeeList')}
          </Typography>
          <PaginatedGrid
            dataTestId="pending-fees-grid"
            columns={columns}
            rows={pendingFees}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>
    </Grid>
  );
};

// Expense Categories Grid Component
export const ExpenseCategoriesGrid: FC = () => {
  const { translateText } = useTranslation();
  const { state, formatCurrency } = useFinancialReports();
  const { expenseCategories } = state;

  return (
    <Grid item xs={12} lg={4}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translateText('finance.reports.expenses.topCategories')}
          </Typography>
          <Stack spacing={{ xs: 1, sm: 2 }} mt={{ xs: 1, sm: 2 }}>
            {expenseCategories.map((category: ExpenseCategory) => (
              <Box
                key={category.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Typography variant="body2">{category.name}</Typography>
                <Chip
                  label={formatCurrency(category.amount)}
                  color={category.color as any}
                  size="small"
                />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};

// Scholarships Grid Component
export const ScholarshipsGrid: FC = () => {
  const { translateText } = useTranslation();
  const { state, formatCurrency } = useFinancialReports();
  const { scholarships } = state;

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'studentName',
      headerName: translateText(
        'finance.reports.scholarships.columns.studentName'
      ),
      width: 200,
    },
    {
      field: 'class',
      headerName: translateText(
        'finance.reports.scholarships.columns.class'
      ),
      width: 120,
    },
    {
      field: 'scholarshipType',
      headerName: translateText(
        'finance.reports.scholarships.columns.scholarshipType'
      ),
      width: 180,
    },
    {
      field: 'amount',
      headerName: translateText(
        'finance.reports.scholarships.columns.amount'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatCurrency(params.row.amount)}
        </Typography>
      ),
    },
    {
      field: 'dateAwarded',
      headerName: translateText(
        'finance.reports.scholarships.columns.dateAwarded'
      ),
      width: 150,
    },
    {
      field: 'status',
      headerName: translateText(
        'finance.reports.scholarships.columns.status'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.status}
          color={
            params.row.status === ScholarshipStatus.DISBURSED
              ? 'success'
              : params.row.status === ScholarshipStatus.APPROVED
                ? 'warning'
                : params.row.status === ScholarshipStatus.PROCESSING
                  ? 'info'
                  : 'error'
          }
          size="small"
        />
      ),
    },
  ], [translateText, formatCurrency]);

  return (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translateText('finance.reports.scholarships.title')}
          </Typography>
          <PaginatedGrid
            dataTestId="scholarships-grid"
            columns={columns}
            rows={scholarships}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>
    </Grid>
  );
};

// Budget vs Actual Grid Component
export const BudgetVsActualGrid: FC = () => {
  const { translateText } = useTranslation();
  const { state, formatCurrency } = useFinancialReports();
  const { budgetItems } = state;

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'category',
      headerName: translateText('finance.reports.budget.columns.category'),
      width: 200,
    },
    {
      field: 'budgetedAmount',
      headerName: translateText(
        'finance.reports.budget.columns.budgetedAmount'
      ),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatCurrency(params.row.budgetedAmount)}
        </Typography>
      ),
    },
    {
      field: 'actualSpent',
      headerName: translateText(
        'finance.reports.budget.columns.actualSpent'
      ),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatCurrency(params.row.actualSpent)}
        </Typography>
      ),
    },
    {
      field: 'variance',
      headerName: translateText('finance.reports.budget.columns.variance'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatCurrency(params.row.variance)}
        </Typography>
      ),
    },
    {
      field: 'variancePercentage',
      headerName: translateText(
        'finance.reports.budget.columns.variancePercentage'
      ),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.row.variancePercentage}%
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: translateText('finance.reports.budget.columns.status'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={
            params.row.status === BudgetStatus.UNDER_BUDGET
              ? translateText(
                'finance.reports.budget.status.underBudget'
              )
              : params.row.status === BudgetStatus.OVER_BUDGET
                ? translateText(
                  'finance.reports.budget.status.overBudget'
                )
                : translateText(
                  'finance.reports.budget.status.onBudget'
                )
          }
          color={
            params.row.status === BudgetStatus.UNDER_BUDGET
              ? 'success'
              : params.row.status === BudgetStatus.OVER_BUDGET
                ? 'error'
                : 'info'
          }
          size="small"
        />
      ),
    },
  ], [translateText, formatCurrency]);

  return (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translateText('finance.reports.budget.title')}
          </Typography>
          <PaginatedGrid
            dataTestId="budget-vs-actual-grid"
            columns={columns}
            rows={budgetItems}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>
    </Grid>
  );
};

// Chart Placeholder Component
interface ChartPlaceholderProps {
  title: string;
  description: string;
  height?: number | { xs: number; sm: number };
}

export const ChartPlaceholder: FC<ChartPlaceholderProps> = ({
  title,
  description,
  height = { xs: 250, sm: 300 },
}) => {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box
          height={height}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="grey.50"
        >
          <Typography color="textSecondary" textAlign="center" sx={{ px: 2 }}>
            {description}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Fee Collection Charts Component
export const FeeCollectionCharts: FC = () => {
  const { translateText } = useTranslation();
  return (
    <>
      <Grid item xs={12} md={6}>
        <ChartPlaceholder
          title={translateText('finance.reports.feeCollection.title')}
          description={translateText(
            'finance.reports.feeCollection.description'
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartPlaceholder
          title={translateText('finance.reports.feeCollection.paymentModes')}
          description={translateText(
            'finance.reports.feeCollection.paymentModesDescription'
          )}
        />
      </Grid>
    </>
  );
};

// Expense Chart Component
export const ExpenseChart: FC = () => {
  const { translateText } = useTranslation();
  return (
    <Grid item xs={12} lg={8}>
      <ChartPlaceholder
        title={translateText('finance.reports.expenses.title')}
        description={translateText('finance.reports.expenses.description')}
        height={{ xs: 300, sm: 400 }}
      />
    </Grid>
  );
};
